import { useEffect, useState } from "react";
import api, { money, shortDate } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const WINDOWS = [7, 14, 30, 60];

export default function TrainingImpact() {
  const [window, setWindow] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/training-reports/impact", { params: { window } }).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [window]);

  const chartData = (data?.rows || []).slice(0, 12).map(r => ({
    name: r.customer_name?.slice(0, 18) || "—",
    before: r.before,
    after: r.after,
  }));
  const uplift = data ? (data.total_after - data.total_before) : 0;
  const upliftPct = data && data.total_before > 0 ? Math.round((uplift / data.total_before) * 100) : null;

  return (
    <div className="space-y-6" data-testid="training-impact-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-4xl font-heading font-black tracking-tight">Training Impact</h1>
          <p className="text-slate-600 mt-1">Sales before vs after each store's training session</p>
          <p className="text-xs text-slate-500 italic mt-1">Correlational — sales are not directly attributed to training.</p>
        </div>
        <select value={window} onChange={(e) => setWindow(Number(e.target.value))} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="impact-window">
          {WINDOWS.map(w => <option key={w} value={w}>{w}-day window</option>)}
        </select>
      </div>

      {loading || !data ? <div>Loading…</div> :
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Kpi label="Total Before Training" value={money(data.total_before)} />
            <Kpi label="Total After Training" value={money(data.total_after)} tone="red" />
            <Kpi label="Estimated Sales Uplift" value={`${uplift >= 0 ? "+" : ""}${money(uplift)}${upliftPct !== null ? ` (${upliftPct}%)` : ""}`} tone={uplift >= 0 ? "good" : "bad"} />
          </div>

          {chartData.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-lg font-heading font-semibold mb-4">Per-Store Comparison</h3>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Legend />
                  <Bar dataKey="before" name="Before Training" fill="#001F54" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="after" name="After Training" fill="#003DA5" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
                  <th className="text-left px-4 py-3">Training Date</th>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Topic</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Agent</th>
                  <th className="text-right px-4 py-3">Attendees</th>
                  <th className="text-right px-4 py-3">Sales Before</th>
                  <th className="text-right px-4 py-3">Sales After</th>
                  <th className="text-right px-4 py-3">Uplift</th>
                </tr></thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.training_id} className="border-t hover:bg-slate-50" data-testid={`impact-row-${r.training_id}`}>
                      <td className="px-4 py-3">{shortDate(r.training_date)}</td>
                      <td className="px-4 py-3 font-semibold">{r.customer_name}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs">{r.training_topic}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs">{r.agent_name}</td>
                      <td className="px-4 py-3 text-right">{r.attendees}</td>
                      <td className="px-4 py-3 text-right">{money(r.before)}</td>
                      <td className="px-4 py-3 text-right text-[#003DA5] font-semibold">{money(r.after)}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${r.uplift_pct == null ? "text-slate-400" : r.uplift_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {r.uplift_pct == null ? "—" : `${r.uplift_pct >= 0 ? "+" : ""}${r.uplift_pct}%`}
                      </td>
                    </tr>
                  ))}
                  {data.rows.length === 0 && <tr><td colSpan="8" className="px-4 py-16 text-center text-slate-500">No training sessions yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      }
    </div>
  );
}
const Kpi = ({ label, value, tone = "" }) => (
  <div className={`bg-white rounded-xl border p-5 ${tone === "red" ? "border-[#003DA5]/30" : tone === "good" ? "border-emerald-200" : tone === "bad" ? "border-red-200" : "border-slate-200"}`}>
    <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</div>
    <div className={`kpi-value text-3xl mt-2 ${tone === "red" ? "text-[#003DA5]" : tone === "good" ? "text-emerald-600" : tone === "bad" ? "text-red-600" : ""}`}>{value}</div>
  </div>
);
